import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useTheme } from '../../../theme/useTheme';
import { AdminColors } from '../constants/adminColors';
import FontFamily from '../../../constants/fonts';

interface AdminButtonProps {
  label: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  onPress?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  small?: boolean;
  loading?: boolean;
}

const AdminButton: React.FC<AdminButtonProps> = ({
  label,
  variant = 'primary',
  onPress,
  icon,
  disabled,
  small,
  loading,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const variantStyle = VARIANTS[variant];
  const isDisabled = disabled || loading;

  const darkContainerStyle = isDark
    ? variant === 'primary'
      ? { backgroundColor: colors.button.primaryBackground }
      : variant === 'secondary'
      ? { backgroundColor: colors.button.secondaryBackground, borderColor: colors.brand.primary }
      : variant === 'ghost'
      ? { borderColor: colors.border.default }
      : undefined
    : undefined;

  const darkLabelStyle = isDark
    ? variant === 'primary' || variant === 'danger'
      ? { color: '#FFFFFF' }
      : variant === 'secondary'
      ? { color: colors.brand.primary }
      : variant === 'ghost'
      ? { color: colors.text.secondary }
      : undefined
    : undefined;

  const activityIndicatorColor = isDark
    ? variant === 'secondary' || variant === 'ghost'
      ? colors.brand.primary
      : colors.button.primaryText
    : variant === 'secondary' || variant === 'ghost'
    ? AdminColors.blue800
    : AdminColors.textOnDark;

  return (
    <TouchableOpacity
      style={[
        styles.btn,
        variantStyle.container,
        darkContainerStyle,
        small && styles.btnSmall,
        isDisabled && styles.btnDisabled,
      ]}
      onPress={onPress}
      activeOpacity={0.8}
      disabled={isDisabled}
    >
      {loading ? (
        <ActivityIndicator
          size="small"
          color={activityIndicatorColor}
        />
      ) : (
        icon && <View style={styles.icon}>{icon}</View>
      )}
      <Text style={[styles.label, variantStyle.label, darkLabelStyle, small && styles.labelSmall]}>
        {loading ? 'Loading...' : label}
      </Text>
    </TouchableOpacity>
  );
};

const VARIANTS = {
  primary: {
    container: { backgroundColor: AdminColors.blue800, borderWidth: 0 },
    label: { color: AdminColors.textOnDark },
  },
  secondary: {
    container: { backgroundColor: AdminColors.bgSurface, borderWidth: 1, borderColor: AdminColors.blue800 },
    label: { color: AdminColors.blue800 },
  },
  danger: {
    container: { backgroundColor: AdminColors.red500, borderWidth: 0 },
    label: { color: AdminColors.textOnDark },
  },
  ghost: {
    container: { backgroundColor: 'transparent', borderWidth: 1, borderColor: AdminColors.gray150 },
    label: { color: AdminColors.gray500 },
  },
};

const styles = StyleSheet.create({
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 8,
    height: 40,
    borderRadius: 24,
    gap: 8,
  },
  btnSmall: {
    paddingHorizontal: 14,
    paddingVertical: 5,
    height: 28,
    borderRadius: 14,
  },
  btnDisabled: {
    opacity: 0.4,
  },
  label: {
    fontSize: 16,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  labelSmall: {
    fontSize: 12,
  },
  icon: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default AdminButton;
